import { Box, Button, CircularProgress } from "@mui/material";
import { useState } from "react";
import { useAtom } from "jotai";
import { fetchScopeLaydownWiseAtom, UpdateScopeAtom } from "../../../atoms/logistics/LogisticsManagementAtom";
import LaydownList from "../LaydownList";
import { ChevronLeft, ChevronRight } from "lucide-react";
import LaydownSvg from "./laydownSVG";
import ScopeSVGComponent from "./scopeSVG";
import { useNavigate } from "react-router-dom";
import {
    Add as AddIcon
} from '@mui/icons-material';

const Scope = (props) => {
    const { laydownList, data, loader, scopeLaydownProgress, currentLaydown, setCurrentLaydown } = props
    const { svgMap, projectList, blocks } = data
    const project_id = localStorage.getItem("project_id");
    const project_name = projectList.find((item) => item.id === parseInt(project_id ?? "0"));
    const navigate = useNavigate();

    const [isEditScope, setIsEditScope] = useState(false)
    const [isSelect, setIsSelect] = useState(false);
    const [scopeLoading, setScopeLoading] = useState(false)
    const [selectPart, setSelectPart] = useState([]);
    const [selectAll, setSelectAll] = useState(false)
    const [visible, setVisible] = useState(true);
    const [, updateScope] = useAtom(UpdateScopeAtom);
    const [, setScopeLaydownWise] = useAtom(fetchScopeLaydownWiseAtom)
    const [enableAddNewLaydown, setEnabelAddNewLaydown] = useState(false)

    const handleToggleEditMode = async () => {
        if (isEditScope) {
            setScopeLoading(true)
            try {
                await updateScope({ laydownId: currentLaydown?.id, blockIds: selectPart })
                await setScopeLaydownWise()
            } catch (err) {
                console.error(err)
            } finally {
                setScopeLoading(false)
                setIsSelect(false)
                setIsEditScope(false)
                setSelectPart([])
            }
        } else {
            setIsEditScope(true)
            setIsSelect(true)
        }
    }

    const handleClickSelectAll = () => {
        setSelectAll(true)
    }

    const handleClickClearSelection = () => {
        setSelectAll(false)
        setSelectPart([])
    }

    const handleClickCancel = () => {
        setIsEditScope(false)
        setIsSelect(false)
        setSelectPart([])
    }

    const handleAddLaydown = () => {
        
    // Clear current laydown and navigate to scope without ID
        setCurrentLaydown({});
        setEnabelAddNewLaydown(true)
        navigate('/dashboard/logistics-management/scope', { replace: true });
    }

    const handleLaydownSelect = (laydown) => {
        // Allow laydown selection only if not in edit mode
        setCurrentLaydown(laydown);
    }

    return (
        <Box sx={{ position: "relative" }}>
            <Box sx={{ display: "flex", alignItems: "stretch", width: "100%" }}>
                <Box sx={{ width: visible ? "80%" : "100%", transition: "width 0.3s ease", height: "calc(100vh - 230px)" }}>
                    {/* If no laydown is selected, show LaydownSvg */}
                    {!currentLaydown || Object.keys(currentLaydown).length === 0 ? (
                        <LaydownSvg
                            currentProject={project_name}
                            scopeLaydownProgress={scopeLaydownProgress}
                            currentLaydown={currentLaydown}
                            setCurrentLaydown={setCurrentLaydown}
                            laydownList={laydownList}
                            svgMap={svgMap}
                            blocks={blocks}
                            enableAddNewLaydown={enableAddNewLaydown}
                            setEnabelAddNewLaydown={setEnabelAddNewLaydown}
                        />
                    ) : (
                        /* If laydown is selected, show scope editing interface */
                        <>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "end", gap: 2 }}>
                                <Button
                                    variant="contained"
                                    onClick={handleAddLaydown}
                                    color="primary"
                                    startIcon={<AddIcon />}
                                    disabled={isEditScope || scopeLoading}
                                    sx={{ mr: 2 }}
                                >
                                    Add New Laydown
                                </Button>
                                <Button variant="outlined" onClick={handleClickSelectAll} disabled={!isEditScope || scopeLoading}>
                                    Select All
                                </Button>
                                <Button variant="outlined" onClick={handleClickClearSelection} disabled={!isEditScope || isSelect === false || scopeLoading}>
                                    Clear Selection
                                </Button>
                                {isEditScope &&
                                    <Button variant="outlined" onClick={handleClickCancel} disabled={scopeLoading}>Cancel</Button>}
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleToggleEditMode}
                                    disabled={scopeLoading}
                                    sx={{ mr: 5 }}
                                >
                                    {scopeLoading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
                                    {isEditScope ? "Save Scope" : "Edit Scope"}
                                </Button >
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
                                {/* Show loading overlay only over the SVG */}
                                {scopeLoading && (
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            backgroundColor: "rgba(255, 255, 255, 0.7)",
                                            zIndex: 10
                                        }}
                                    >
                                        <CircularProgress />
                                    </Box>
                                )}
                                <ScopeSVGComponent
                                    loader={loader}
                                    svgMap={svgMap}
                                    blocks={blocks}
                                    selectPart={selectPart}
                                    setSelectPart={setSelectPart}
                                    isSelect={isSelect}
                                    scopeLaydownProgress={scopeLaydownProgress}
                                    currentLaydownId={currentLaydown?.id}
                                    selectAll={selectAll}
                                />
                            </Box>
                        </>
                    )}
                </Box>

                {visible && (
                    <Box
                        sx={{
                            width: "20%",
                            height: "calc(100vh - 260px)",
                            borderLeft: "1px solid #ccc",
                            paddingLeft: 2,
                            transition: "width 0.3s ease",
                        }}
                    >
                        <LaydownList
                            currentLaydown={currentLaydown}
                            setCurrentLaydown={handleLaydownSelect} // Use custom handler to check edit mode
                            laydownList={laydownList}
                            isEditMode={isEditScope} // Pass edit mode status to LaydownList
                        />
                    </Box>
                )}

                {/* Toggle Button */}
                <Box
                    onClick={() => setVisible(!visible)}
                    sx={{
                        height: "24px",
                        border: "1px solid #e0e0e0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "white",
                        zIndex: 10,
                        position: "absolute",
                        top: 0,
                        right: visible ? "20%" : 0,
                        width: "24px",
                        cursor: "pointer",
                        transition: "right 0.3s ease",
                    }}
                >
                    {visible ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
                </Box>
            </Box>

        </Box>
    );
};

export default Scope;